import { getEventDisplayConfig } from "@arti/dashboard-events";
import { type FC, useState } from "react";

import type {
  HydrationStatus,
  ProviderProgressEntry,
} from "../../hooks/useProviderProgress";
import type {
  RepositoryChatTurnStatusSnapshot,
  RetrievalStatus,
} from "./repositoryChatModels";

import { formatRelativeTime } from "../../utils/time";
import { ActivePulseIndicator } from "../common/ActivePulseIndicator";
import { LoadingSpinner } from "../common/LoadingState";
import { HIDDEN_EVENT_TYPES } from "../dashboard/providerProgress";
import { getEventSummary } from "../dashboard/providerProgress/eventSummary";

interface RepositoryChatTaskStatusProps {
  events: ProviderProgressEntry[];
  hydrationStatus: HydrationStatus;
  isBusy: boolean;
  repositoryCount: number;
  turnSnapshot: RepositoryChatTurnStatusSnapshot | null;
  retrievalStatus: RetrievalStatus | null;
}

export const RepositoryChatTaskStatus: FC<RepositoryChatTaskStatusProps> = ({
  events,
  hydrationStatus,
  isBusy,
  repositoryCount,
  turnSnapshot,
  retrievalStatus,
}) => {
  const resolvedStatus =
    retrievalStatus ??
    fallbackRetrievalStatus(turnSnapshot) ??
    buildProvisionalStatus(isBusy);
  if (!resolvedStatus) {
    return null;
  }

  const activeStatus = inferEffectiveStatus(resolvedStatus, events);
  const matchingSnapshot = resolveMatchingSnapshot(turnSnapshot, activeStatus);

  const latestEvent = events
    .toReversed()
    .find(
      (event) =>
        !HIDDEN_EVENT_TYPES.has(event.event_type) &&
        getEventDisplayConfig(event.event_type),
    );
  const latestEventConfig = latestEvent
    ? getEventDisplayConfig(latestEvent.event_type)
    : null;
  const latestEventSummary = latestEvent ? getEventSummary(latestEvent) : null;
  const lastUpdatedMs = resolveLastUpdatedMs(latestEvent, matchingSnapshot);
  const latestMilestoneDetail =
    latestEventSummary ??
    resolveLatestMilestoneDetail({
      activeStatus,
      hydrationStatus,
      repositoryCount,
    });

  const milestoneLabel = latestEventConfig
    ? latestEventConfig.label
    : resolveLatestMilestoneLabel(activeStatus, hydrationStatus);

  const busy = isBusyStatus(activeStatus.status);

  return (
    <InlineStatusBubble
      activeStatus={activeStatus}
      busy={busy}
      events={events}
      lastUpdatedMs={lastUpdatedMs}
      latestMilestoneDetail={latestMilestoneDetail}
      milestoneLabel={milestoneLabel}
    />
  );
};

const InlineStatusBubble: FC<{
  activeStatus: RetrievalStatus;
  busy: boolean;
  events: ProviderProgressEntry[];
  lastUpdatedMs: number | null;
  latestMilestoneDetail: null | string;
  milestoneLabel: string;
}> = ({
  activeStatus,
  busy,
  events,
  lastUpdatedMs,
  latestMilestoneDetail,
  milestoneLabel,
}) => {
  const [expanded, setExpanded] = useState(false);
  const statusTone = getInlineStatusTone(activeStatus.status);
  const visibleEvents = deduplicateEventsByLatestType(
    events.filter(
      (event) =>
        !HIDDEN_EVENT_TYPES.has(event.event_type) &&
        getEventDisplayConfig(event.event_type),
    ),
  );
  const latestVisibleEvent = visibleEvents.at(-1);
  const latestVisibleLabel = latestVisibleEvent
    ? getEventDisplayConfig(latestVisibleEvent.event_type)?.label
    : null;

  return (
    <article className="flex justify-start">
      <div
        className={`w-full border shadow-sm sm:w-auto sm:max-w-4xl ${statusTone.border} ${statusTone.bg}`}
      >
        <button
          className="flex w-full flex-col gap-1.5 px-3 py-2.5 text-left sm:px-4"
          onClick={() => setExpanded((previous) => !previous)}
          type="button"
        >
          {/* Row 1: Status label + milestone + chevron */}
          <span className="flex w-full items-center gap-2.5">
            <span className="flex items-center gap-2">
              {busy ? (
                <LoadingSpinner size="sm" />
              ) : (
                <StatusIcon status={activeStatus.status} />
              )}
              <span
                className={`font-mono text-[10px] font-bold uppercase tracking-[0.2em] ${statusTone.label}`}
              >
                Arti
              </span>
            </span>

            <span className={`text-xs sm:text-sm ${statusTone.text}`}>
              {milestoneLabel}
            </span>

            {lastUpdatedMs ? (
              <span className="ml-auto hidden whitespace-nowrap text-[10px] opacity-50 lg:inline">
                {formatRelativeTime(lastUpdatedMs)}
              </span>
            ) : null}

            <svg
              className={`ml-auto h-3 w-3 flex-shrink-0 opacity-40 transition-transform duration-150 sm:ml-0 ${expanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                d="M19 9l-7 7-7-7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>

          {/* Row 2: Mini step trail (collapsed glanceable progress) */}
          {visibleEvents.length > 0 ? (
            <span className="flex items-center gap-1.5 pl-5">
              {visibleEvents.map((event, index) => {
                const isLast = index === visibleEvents.length - 1;
                return (
                  <span
                    key={`${event.event_type}-${index}`}
                    className="flex items-center gap-1.5"
                  >
                    <span
                      className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${isLast && busy ? `${statusTone.dot} animate-pulse` : statusTone.dot}`}
                    />
                    {isLast && latestVisibleLabel ? (
                      <span
                        className={`text-[10px] ${statusTone.text} opacity-70`}
                      >
                        {latestVisibleLabel}
                      </span>
                    ) : null}
                    {!isLast ? (
                      <span
                        className={`h-px w-2 ${statusTone.dot} opacity-30`}
                      />
                    ) : null}
                  </span>
                );
              })}
              <span className="ml-1 text-[10px] opacity-40">
                {visibleEvents.length}{" "}
                {visibleEvents.length === 1 ? "step" : "steps"}
              </span>
            </span>
          ) : null}
        </button>

        {expanded ? (
          <div
            className={`space-y-2 border-t px-3 py-2.5 sm:px-4 ${statusTone.divider}`}
          >
            <p className={`text-xs sm:text-sm ${statusTone.text}`}>
              {activeStatus.message}
            </p>

            {latestMilestoneDetail ? (
              <p className="text-xs leading-relaxed opacity-60">
                {latestMilestoneDetail}
              </p>
            ) : null}

            {activeStatus.taskId ? (
              <span className="inline-block font-mono text-[10px] opacity-40">
                Task {shortTaskId(activeStatus.taskId)}
              </span>
            ) : null}

            {visibleEvents.length > 0 ? (
              <div className="mt-1 space-y-1">
                {visibleEvents.slice(-8).map((event, index) => {
                  const config = getEventDisplayConfig(event.event_type);
                  const summary = getEventSummary(event);
                  return (
                    <div
                      key={`${event.event_type}-${index}`}
                      className="flex items-center gap-2 text-[11px] opacity-70"
                    >
                      <span
                        className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${statusTone.dot}`}
                      />
                      <span className="font-medium">
                        {config?.label ?? event.event_type}
                      </span>
                      {summary ? (
                        <span className="truncate opacity-70">{summary}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
};

const StatusIcon: FC<{ status: RetrievalStatus["status"] }> = ({ status }) => {
  switch (status) {
    case "succeeded": {
      return (
        <svg
          className="h-3.5 w-3.5 text-emerald-500"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          viewBox="0 0 24 24"
        >
          <path
            d="M5 13l4 4L19 7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    case "failed": {
      return (
        <svg
          className="h-3.5 w-3.5 text-rose-500"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          viewBox="0 0 24 24"
        >
          <path
            d="M6 18L18 6M6 6l12 12"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    case "timed_out": {
      return (
        <svg
          className="h-3.5 w-3.5 text-amber-500"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          viewBox="0 0 24 24"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    default: {
      return <ActivePulseIndicator size="sm" />;
    }
  }
};

function getInlineStatusTone(status: RetrievalStatus["status"]) {
  switch (status) {
    case "failed": {
      return {
        bg: "bg-rose-50 dark:bg-rose-950/30",
        border: "border-rose-200 dark:border-rose-800/60",
        divider: "border-rose-200/60 dark:border-rose-800/40",
        dot: "bg-rose-400",
        label: "text-rose-600 dark:text-rose-400",
        text: "text-rose-700 dark:text-rose-200",
      };
    }
    case "succeeded": {
      return {
        bg: "bg-emerald-50 dark:bg-emerald-950/30",
        border: "border-emerald-200 dark:border-emerald-800/60",
        divider: "border-emerald-200/60 dark:border-emerald-800/40",
        dot: "bg-emerald-400",
        label: "text-emerald-600 dark:text-emerald-400",
        text: "text-emerald-700 dark:text-emerald-200",
      };
    }
    case "timed_out": {
      return {
        bg: "bg-amber-50 dark:bg-amber-950/30",
        border: "border-amber-200 dark:border-amber-800/60",
        divider: "border-amber-200/60 dark:border-amber-800/40",
        dot: "bg-amber-400",
        label: "text-amber-600 dark:text-amber-400",
        text: "text-amber-700 dark:text-amber-200",
      };
    }
    default: {
      return {
        bg: "bg-slate-50 dark:bg-slate-900/60",
        border: "border-slate-200 dark:border-slate-700",
        divider: "border-slate-200/60 dark:border-slate-700/60",
        dot: "bg-emerald-400",
        label: "text-slate-500 dark:text-slate-400",
        text: "text-slate-700 dark:text-slate-200",
      };
    }
  }
}

/**
 * If the reported status is "pending" or "queued" but provider events are
 * already flowing, the task is actually running — the SSE stream just
 * didn't push an updated status. Promote to "running" so the UI reflects
 * reality.
 */
function inferEffectiveStatus(
  status: RetrievalStatus,
  events: ProviderProgressEntry[],
): RetrievalStatus {
  if (
    (status.status === "pending" || status.status === "queued") &&
    events.length > 0
  ) {
    return {
      ...status,
      message: "Repository retrieval is running.",
      status: "running",
    };
  }

  return status;
}

function fallbackRetrievalStatus(
  snapshot: RepositoryChatTurnStatusSnapshot | null,
): RetrievalStatus | null {
  if (!snapshot) {
    return null;
  }

  return {
    message: buildSnapshotStatusMessage(snapshot.status),
    status: snapshot.status,
    taskId: snapshot.taskId,
    turnId: snapshot.turnId,
  };
}

function resolveMatchingSnapshot(
  snapshot: RepositoryChatTurnStatusSnapshot | null,
  activeStatus: RetrievalStatus,
): RepositoryChatTurnStatusSnapshot | null {
  if (!snapshot) {
    return null;
  }

  return snapshot.turnId === activeStatus.turnId ? snapshot : null;
}

function buildProvisionalStatus(isBusy: boolean): RetrievalStatus | null {
  if (!isBusy) {
    return null;
  }

  return {
    message: "Handing off to repository retrieval.",
    status: "pending",
    taskId: null,
    turnId: "pending",
  };
}

function buildSnapshotStatusMessage(status: RetrievalStatus["status"]): string {
  switch (status) {
    case "failed": {
      return "Repository retrieval failed.";
    }
    case "pending": {
      return "Repository retrieval is preparing.";
    }
    case "queued": {
      return "Repository retrieval is queued.";
    }
    case "running": {
      return "Repository retrieval is still running.";
    }
    case "succeeded": {
      return "Repository retrieval completed.";
    }
    case "timed_out": {
      return "Repository retrieval timed out.";
    }
  }
}

function isBusyStatus(status: RetrievalStatus["status"]): boolean {
  return status === "pending" || status === "queued" || status === "running";
}

function resolveLatestMilestoneLabel(
  status: RetrievalStatus,
  hydrationStatus: HydrationStatus,
): string {
  if (hydrationStatus === "loading") {
    return "Hydrating task activity…";
  }

  switch (status.status) {
    case "failed": {
      return "Retrieval failed";
    }
    case "pending": {
      return "Preparing retrieval…";
    }
    case "queued": {
      return "Waiting for capacity…";
    }
    case "running": {
      return "Retrieving evidence…";
    }
    case "succeeded": {
      return "Evidence ready";
    }
    case "timed_out": {
      return "Retrieval timed out";
    }
  }
}

function resolveLatestMilestoneDetail({
  activeStatus,
  hydrationStatus,
  repositoryCount,
}: {
  activeStatus: RetrievalStatus;
  hydrationStatus: HydrationStatus;
  repositoryCount: number;
}): null | string {
  if (hydrationStatus === "loading") {
    return "Hydrating task activity…";
  }

  switch (activeStatus.status) {
    case "pending": {
      return `Preparing evidence retrieval across ${formatRepositoryCount(repositoryCount)}.`;
    }
    case "queued": {
      return "Queued for the next available retrieval worker.";
    }
    case "running": {
      return "Repository evidence is being collected now.";
    }
    case "succeeded": {
      return "Evidence has been submitted back to the chat response.";
    }
    case "failed": {
      return "The retrieval worker reported a failure before evidence was submitted.";
    }
    case "timed_out": {
      return "The retrieval worker did not complete within the current wait budget.";
    }
  }
}

function formatRepositoryCount(repositoryCount: number): string {
  if (repositoryCount === 1) {
    return "1 selected repository";
  }

  return `${repositoryCount} selected repositories`;
}

function shortTaskId(taskId: string): string {
  return taskId.length <= 28 ? taskId : `${taskId.slice(0, 28)}…`;
}

export function deduplicateEventsByLatestType(
  events: ProviderProgressEntry[],
): ProviderProgressEntry[] {
  const seen = new Set<string>();
  const deduplicated: ProviderProgressEntry[] = [];

  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (!event || seen.has(event.event_type)) {
      continue;
    }

    seen.add(event.event_type);
    deduplicated.push(event);
  }

  deduplicated.reverse();
  return deduplicated;
}

function resolveLastUpdatedMs(
  latestEvent: ProviderProgressEntry | undefined,
  snapshot: RepositoryChatTurnStatusSnapshot | null,
): number | null {
  if (latestEvent?.timestamp) {
    return latestEvent.timestamp;
  }

  if (latestEvent?.received_at) {
    return latestEvent.received_at;
  }

  if (!snapshot) {
    return null;
  }

  const timestamp = Date.parse(snapshot.updatedAt);
  return Number.isNaN(timestamp) ? null : timestamp;
}
